import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal, { ModalBody, ModalFooter } from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import { apiClient } from '@/lib';
import type { BooksListResponse, Publisher } from '@/types/api';

interface PublisherLookupResponse {
  pub_id: string;
  pub_name: string;
}

interface LocationState {
  pubName?: string;
}

export default function DistPublisherDetailPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const state = (location.state || {}) as LocationState;

  const [publisherName, setPublisherName] = useState<string | null>(state.pubName ?? null);
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [defaultLanguage, setDefaultLanguage] = useState<string | null>(null);
  const [autoPublish, setAutoPublish] = useState<boolean | null>(null);

  const [totalBooks, setTotalBooks] = useState<number | null>(null);
  const [publishedCount, setPublishedCount] = useState<number | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [failedCount, setFailedCount] = useState<number | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pubId, setPubId] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch publisher details by ID (for name/status/config) if not provided via navigation state
  useEffect(() => {
    let isCancelled = false;

    async function fetchPublisherDetails() {
      if (!id) return;

      try {
        const response = await apiClient.get<{ id: string; publisher: Publisher }>(
          `distribution/publishers/${id}`
        );

        if (isCancelled) return;

        const publisher = response.publisher ?? (response as unknown as Publisher);

        const resolvedName =
          publisher.name || (publisher as { pub_name?: string }).pub_name || publisher.id;
        if (!publisherName) {
          setPublisherName(resolvedName);
        }

        setStatus(publisher.active === false ? 'inactive' : 'active');

        const config = (publisher as unknown as { config_json?: Record<string, unknown> }).config_json;
        if (config && typeof config === 'object') {
          const dl = (config as { default_language?: string }).default_language;
          const ap = (config as { auto_publish?: boolean }).auto_publish;
          if (dl) setDefaultLanguage(dl);
          if (typeof ap === 'boolean') setAutoPublish(ap);
        }
      } catch (err) {
        if (isCancelled) return;
        console.error('Failed to load publisher details', err);
      }
    }

    // If we already have a publisher name from state, details fetch is optional
    if (!publisherName) {
      fetchPublisherDetails();
    } else if (id) {
      // Still try to fetch details to populate config/status if possible
      fetchPublisherDetails();
    }

    return () => {
      isCancelled = true;
    };
  }, [id, publisherName]);

  // Fetch stats from Distribution Worker via backend proxy
  useEffect(() => {
    let isCancelled = false;

    async function fetchStats() {
      if (!publisherName) return;

      try {
        setIsLoading(true);
        setError(null);

        // 1) Resolve Distribution publisher ID from name
        const lookup = await apiClient.post<PublisherLookupResponse>(
          'distribution/publishers/lookup-id',
          { pub_name: publisherName }
        );

        if (isCancelled) return;

        const resolvedPubId = lookup.pub_id;
        if (!resolvedPubId) {
          throw new Error('Publisher ID not found for this name');
        }

        setPubId(resolvedPubId);

        const base = `distribution/books?publisherId=${encodeURIComponent(resolvedPubId)}`;

        // 2) Fetch totals for all, published, pending, failed
        const [allRes, publishedRes, pendingRes, failedRes] = await Promise.all([
          apiClient.get<BooksListResponse>(base),
          apiClient.get<BooksListResponse>(`${base}&status=PUBLISHED`),
          apiClient.get<BooksListResponse>(`${base}&status=PENDING`),
          apiClient.get<BooksListResponse>(`${base}&status=FAILED`),
        ]);

        if (isCancelled) return;

        setTotalBooks(allRes.total ?? 0);
        setPublishedCount(publishedRes.total ?? 0);
        setPendingCount(pendingRes.total ?? 0);
        setFailedCount(failedRes.total ?? 0);
      } catch (err) {
        if (isCancelled) return;
        console.error('Failed to load publisher stats', err);
        const message = err instanceof Error ? err.message : 'Failed to load publisher stats';
        setError(message);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchStats();

    return () => {
      isCancelled = true;
    };
  }, [publisherName]);

  const handleSavePublisherName = async () => {
    if (!pubId) {
      setEditError('Publisher ID is not yet available. Please wait a moment and try again.');
      return;
    }

    const trimmed = editName.trim();
    if (!trimmed) {
      setEditError('Publisher name is required');
      return;
    }

    try {
      setIsSaving(true);
      setEditError(null);

      await apiClient.patch(`distribution/publishers/simple/${pubId}`, {
        pub_name: trimmed,
      });

      setPublisherName(trimmed);
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update publisher name', err);
      const message = err instanceof Error ? err.message : 'Failed to update publisher name';
      setEditError(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Link
        to="/distribution/publishers"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Publishers
      </Link>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {publisherName || 'Publisher'}
          </h1>
          <Badge
            variant={status === 'active' ? 'success' : 'warning'}
            className="mt-2"
          >
            {status === 'active' ? 'Active' : 'Inactive'}
          </Badge>
        </div>
        <Button
          variant="outline"
          leftIcon={<PencilIcon className="w-4 h-4" />}
          onClick={() => {
            setEditName(publisherName || '');
            setEditError(null);
            setIsEditModalOpen(true);
          }}
        >
          Edit Publisher
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-slate-800">
            {totalBooks !== null ? totalBooks : '—'}
          </p>
          <p className="text-sm text-slate-500">Total Books</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-success-500">
            {publishedCount !== null ? publishedCount : '—'}
          </p>
          <p className="text-sm text-slate-500">Published</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-warning-500">
            {pendingCount !== null ? pendingCount : '—'}
          </p>
          <p className="text-sm text-slate-500">Pending</p>
        </CardBody></Card>
        <Card><CardBody className="text-center">
          <p className="text-3xl font-bold text-danger-500">
            {failedCount !== null ? failedCount : '—'}
          </p>
          <p className="text-sm text-slate-500">Failed</p>
        </CardBody></Card>
      </div>

      {isLoading && (
        <p className="text-sm text-slate-400">Loading publisher statistics...</p>
      )}
      {error && !isLoading && (
        <p className="text-sm text-danger-500">Error loading stats: {error}</p>
      )}

      <Card>
        <CardHeader title="Configuration" />
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">Default Language</p>
              <p className="font-medium">{defaultLanguage || '—'}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Auto Publish</p>
              <p className="font-medium">
                {autoPublish == null ? '—' : autoPublish ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Edit Publisher Name Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          if (isSaving) return;
          setIsEditModalOpen(false);
          setEditError(null);
          setEditName(publisherName || '');
        }}
        title="Edit Publisher"
        description="Update the publisher name."
        size="sm"
      >
        <ModalBody>
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Publisher Name
            </label>
            <Input
              placeholder="Enter publisher name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              autoFocus
            />
            {editError && (
              <p className="text-sm text-danger-500">{editError}</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (isSaving) return;
              setIsEditModalOpen(false);
              setEditError(null);
              setEditName(publisherName || '');
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePublisherName}
            isLoading={isSaving}
            disabled={isSaving || !editName.trim()}
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

